import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Company, CompanyWithDocuments } from './types';
import type { RootState } from '../index';

interface CompanyState {
  selectedCompanies: Company[];
  loadedCompanyDocs: CompanyWithDocuments[];
  expandedCompanyIds: string[];
  expandedCategories: string[];
  selectedDocumentType: string | null;
}

const initialState: CompanyState = {
  selectedCompanies: [],
  loadedCompanyDocs: [],
  expandedCompanyIds: [],
  expandedCategories: [],
  selectedDocumentType: null,
};

export const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setSelectedCompanies: (state, action: PayloadAction<Company[]>) => {
      state.selectedCompanies = action.payload;
    },
    setLoadedCompanyDocs: (state, action: PayloadAction<CompanyWithDocuments[]>) => {
      state.loadedCompanyDocs = action.payload;
    },
    toggleCompanyExpanded: (state, action: PayloadAction<string>) => {
      const index = state.expandedCompanyIds.indexOf(action.payload);
      if (index === -1) {
        state.expandedCompanyIds.push(action.payload);
      } else {
        state.expandedCompanyIds.splice(index, 1);
      }
    },
    toggleCategoryExpanded: (state, action: PayloadAction<string>) => {
      const index = state.expandedCategories.indexOf(action.payload);
      if (index === -1) {
        state.expandedCategories.push(action.payload);
      } else {
        state.expandedCategories.splice(index, 1);
      }
    },
    setSelectedDocumentType: (state, action: PayloadAction<string | null>) => {
      state.selectedDocumentType = action.payload;
    },
    resetCompanyState: () => initialState,
  },
});

export const {
  setSelectedCompanies,
  setLoadedCompanyDocs,
  toggleCompanyExpanded,
  toggleCategoryExpanded,
  setSelectedDocumentType,
  resetCompanyState,
} = companySlice.actions;

export default companySlice.reducer;

// Selectors
export const selectHasNonEmbeddedSelection = (state: RootState): boolean =>
  state.company.selectedCompanies.some((c) => c.embedded === false);

export const selectAllSelectedCompaniesEmbedded = (state: RootState): boolean =>
  state.company.selectedCompanies.length > 0 &&
  state.company.selectedCompanies.every((c) => c.embedded !== false);
